export interface ReasoningSection {
  index: number;
  label: string;
  content: string;
}

export type ReasoningBlock = {
  number: number;
  title: string;
  content: string;
};

export type ReasoningSectionLabels = Record<number, string>;

export const PHASE_ONE_REASONING_SECTION_LABELS: ReasoningSectionLabels = {
  1: "MARKET_QUALITY",
  2: "CURRENT_STRUCTURE (30m / 15m)",
  3: "LIQUIDITY_EVENTS",
  4: "HIGHER_TIMEFRAME_CONTEXT",
  5: "EXECUTION_CONDITIONS_DESCRIPTION",
  6: "STRUCTURAL_CONFLUENCE",
  7: "CONTEXT_SNAPSHOT",
  8: "HYPOTHESIS_LOGIC",
  9: "CONFIRMATION_PLAN",
};

export const PHASE_TWO_REASONING_SECTION_LABELS: ReasoningSectionLabels = {
  1: "MARKET_QUALITY",
  2: "CURRENT_STRUCTURE (30m / 15m)",
  3: "LIQUIDITY_EVENTS",
  4: "HIGHER_TIMEFRAME_CONTEXT",
  5: "EXECUTION_QUALITY",
  6: "STRUCTURAL_CONFLUENCE",
  7: "MEMORY_SNAPSHOT",
  8: "AUDIT_VS_PRIOR_VIEW",
  9: "DECISION_LOGIC",
  10: "EXECUTION_PLAN",
};

export const DEFAULT_REASONING_SECTION_LABELS: ReasoningSectionLabels = PHASE_TWO_REASONING_SECTION_LABELS;

export function getReasoningSectionLabels(phaseNumber?: number | null): ReasoningSectionLabels {
  if (phaseNumber === 1) return PHASE_ONE_REASONING_SECTION_LABELS;
  return PHASE_TWO_REASONING_SECTION_LABELS;
}

function normalizeTitleKey(input: string): string {
  return String(input || '')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[^A-Z0-9]+/g, '');
}

function normalizeTitleKeyLoose(input: string): string {
  // Remove parentheticals so "AUDIT VS PRIOR VIEW" matches
  // "AUDIT VS PRIOR VIEW (>=3 items)".
  const noParen = String(input || '').replace(/\([^)]*\)/g, ' ');
  return normalizeTitleKey(noParen);
}

function isLikelySectionTitle(
  sectionNumber: number,
  maybeTitle: string,
  sectionLabels: ReasoningSectionLabels = DEFAULT_REASONING_SECTION_LABELS,
): boolean {
  const canonical = sectionLabels[sectionNumber];
  if (!canonical) return false;
  const aStrict = normalizeTitleKey(maybeTitle);
  const bStrict = normalizeTitleKey(canonical);
  if (aStrict && bStrict && aStrict === bStrict) return true;

  const aLoose = normalizeTitleKeyLoose(maybeTitle);
  const bLoose = normalizeTitleKeyLoose(canonical);
  if (aLoose && bLoose && aLoose === bLoose) return true;

  return false;
}

function normalizeBracketedSections(
  core: string,
  sectionLabels: ReasoningSectionLabels = DEFAULT_REASONING_SECTION_LABELS,
): string {
  // Turn "[1] ... [2] ..." into real line-start headers.
  // This MUST NOT delete real content (some sections have early ':' tokens).
  let s = core.replace(/\r\n/g, "\n");

  // Ensure each [N] begins on a new line (if it appears mid-paragraph).
  s = s.replace(/\s*\[(\d{1,2})\]\s*/g, "\n[$1] ");
  s = s.replace(/^\n+/, "");

  const lines = s.split("\n");
  const out: string[] = [];

  for (const line of lines) {
    const m = line.match(/^\[(\d{1,2})\]\s*(.*)$/);
    if (!m) {
      out.push(line);
      continue;
    }
    const num = parseInt(m[1], 10);
    const restRaw = (m[2] ?? "").trim();

    out.push(`[${num}]`);

    if (!restRaw) continue;

    // If the remainder starts with a title-like prefix ending with a colon,
    // remove that prefix ONLY when it matches the canonical section label.
    // Example: "MARKETQUALITY: Market is active..." -> "Market is active..."
    const titleColon = restRaw.match(/^([A-Za-z][A-Za-z0-9\s/&()_\-]{0,90}):\s*(.*)$/);
    if (titleColon) {
      const maybeTitle = titleColon[1].trim();
      const after = (titleColon[2] ?? "").trim();
      if (isLikelySectionTitle(num, maybeTitle, sectionLabels)) {
        if (after) out.push(after);
        continue;
      }
    }

    // Otherwise keep the remainder as content.
    out.push(restRaw);
  }

  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Parse numbered reasoning blocks like the fixed 10-point schema:
 * 1. MARKET QUALITY
 * 2. DTF CONTEXT
 * ...
 * 10. EXECUTION PLAN (SL/TP calculation)
 *
 * We treat any line that starts with "N." or "N)" as a section header
 * and capture everything until the next header (or end of text) as that
 * section's content. Titles in the UI always come from SECTION_LABELS so
 * GPT formatting quirks do not break the structure.
 */
export function parseReasoningSections(
  raw: string | null | undefined,
  options?: { sectionLabels?: ReasoningSectionLabels },
): ReasoningSection[] {
  const blocks = parseReasoningBlocks(raw, options);
  if (!blocks.length) return [];
  return blocks
    .map((b) => ({
      index: b.number,
      label: b.title,
      content: b.content,
    }))
    .sort((a, b) => a.index - b.index);
}

/**
 * Core block parser used by the UI components.
 *
 * Implementation notes:
 * - Only trusts headers at the start of a line: "^N." or "^N)".
 * - Does NOT try to detect inline headers inside sentences to avoid
 *   accidentally cutting content.
 * - Canonical section titles come from SECTION_LABELS; the raw header
 *   line from GPT is ignored for display but still used to locate blocks.
 */
export function parseReasoningBlocks(
  raw: string | null | undefined,
  options?: { sectionLabels?: ReasoningSectionLabels },
): ReasoningBlock[] {
  const text = (raw ?? "").trim();
  if (!text) return [];
  const sectionLabels = options?.sectionLabels ?? DEFAULT_REASONING_SECTION_LABELS;
  
  // If REASONING_START / REASONING_END markers are present (as in the
  // backend prompt), restrict parsing strictly to that inner block so
  // we don't accidentally treat other numbered lists as sections.
  let core = text.replace(/\r\n/g, "\n");

  const startMarker = "[REASONING_START]";
  const endMarker = "[REASONING_END]";
  const startIdx = core.indexOf(startMarker);
  const endIdx = core.indexOf(endMarker);

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    core = core.slice(startIdx + startMarker.length, endIdx).trim();
  }

  let normalized = normalizeBracketedSections(core, sectionLabels);

  // Find all header lines:
  // - "N. ..." or "N) ..." at the start of a line
  // - "[N]" at the start of a line
  // Important: do NOT use "\s*" here because in JS it also matches newlines.
  // If we allow newlines, a header like "[1]\nMarket ..." gets swallowed into the
  // header match, leaving an empty content block.
  const headerRegex = /^(?:\[(\d{1,2})\][ \t]*|(?:(\d{1,2})[\.).][ \t]*)).*$/gm;

  type HeaderMatch = { number: number; start: number; contentStart: number };
  const headers: HeaderMatch[] = [];

  let m: RegExpExecArray | null;
  while ((m = headerRegex.exec(normalized)) !== null) {
    const numStr = m[1] ?? m[2];
    const num = parseInt(String(numStr), 10);
    if (!Number.isFinite(num)) continue;

    const fullLine = m[0] || "";
    const start = m.index;
    const contentStart = start + fullLine.length;

    headers.push({ number: num, start, contentStart });
  }

  if (!headers.length) return [];

  const blocks: ReasoningBlock[] = [];

  for (let i = 0; i < headers.length; i++) {
    const current = headers[i];
    const next = headers[i + 1];
    const end = next ? next.start : normalized.length;

    let rawContent = normalized.slice(current.contentStart, end).trim();

    // Always use canonical UI labels; GPT header text is ignored for title
    const title = sectionLabels[current.number] ?? `Section ${current.number}`;

    // Remove redundant title lines that sometimes appear right after the header,
    // e.g. "[1]\nMARKET QUALITY\n..." or "[1]\nMARKETQUALITY\n...".
    if (rawContent) {
      const lines = rawContent.replace(/\r\n/g, "\n").split("\n");
      const first = (lines[0] ?? "").trim();
      if (first && isLikelySectionTitle(current.number, first, sectionLabels)) {
        const rest = lines.slice(1).join("\n").trim();
        rawContent = rest;
      }
    }

    blocks.push({
      number: current.number,
      title,
      content: rawContent,
    });
  }

  return blocks;
}
